<!--
  NewsletterSend.svelte — Pre-send staging page for newsletter campaigns.

  Shows recipient counts, bounce/opt-out exclusions, batch configuration,
  timing estimates, and a confirmation button before actually dispatching.
  Reached via the "Send" button on AdminNewsletters which navigates here
  instead of directly triggering the send.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Button,
		Spinner,
		Heading,
		P,
		Badge,
		Hr,
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell
	} from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { push } from '../../abstract-spa-router';
	import {
		getNewsletterStaging,
		getNewsletter,
		sendNewsletter,
		previewNewsletter,
		testSendNewsletter
	} from '../../AppNetwork';
	import type { Newsletter, NewsletterStagingData } from '../../AppTypes';
	import { toastSuccess, toastError } from '../../toast';
	import { ConfirmButton } from '../../components';
	import { t, t2 } from '../../i18n';

	interface Props {
		/** Newsletter ID extracted from the route parameter. */
		params?: { id?: string };
	}

	let { params = {} }: Props = $props();

	/** The newsletter being staged. */
	let newsletter: Newsletter | null = $state(null);
	/** Staging data from the server. */
	let staging: NewsletterStagingData | null = $state(null);
	/** Loading flag. */
	let loading = $state(true);
	/** Error string. */
	let error = $state('');
	/** Sending in progress. */
	let sending = $state(false);
	/** Preview HTML (loaded on demand). */
	let previewHtml = $state('');
	/** Whether preview is open. */
	let showPreview = $state(false);

	let newsletterId = $derived(params.id ? parseInt(params.id) : 0);

	onMount(async () => {
		if (!newsletterId) {
			error = t2('Invalid newsletter ID.');
			loading = false;
			return;
		}
		await loadData();
	});

	/**
	 *
	 */
	async function loadData() {
		loading = true;
		error = '';
		/** Fetch newsletter and staging data in parallel. */
		try {
			const [nl, st] = await Promise.all([getNewsletter(newsletterId), getNewsletterStaging(newsletterId)]);
			newsletter = nl;
			staging = st;
		} catch (err: any) {
			error = err?.message || t2('Failed to load staging data.');
		} finally {
			loading = false;
		}
	}

	/**
	 *
	 */
	async function doSend() {
		if (!newsletterId || sending) return;
		sending = true;
		/** Dispatch the newsletter and navigate back to the list on success. */
		try {
			await sendNewsletter(newsletterId);
			toastSuccess(t2('Newsletter dispatch started.'));
			push('/Newsletters');
		} catch (err: any) {
			toastError(err?.message || t2('Failed to send newsletter.'));
		} finally {
			sending = false;
		}
	}

	/**
	 *
	 */
	async function doPreview() {
		try {
			const result = await previewNewsletter(newsletterId);
			previewHtml = result.html;
			showPreview = true;
		} catch (err: any) {
			toastError(err?.message || t2('Failed to load preview.'));
		}
	}

	/**
	 *
	 */
	async function doTestSend() {
		try {
			await testSendNewsletter(newsletterId);
			toastSuccess(t2('Test email sent to your address.'));
		} catch (err: any) {
			toastError(err?.message || t2('Failed to send test email.'));
		}
	}

	/**
	 *
	 */
	function goBack() {
		push('/Newsletters');
	}

	/**
	 *
	 */
	function audienceLabel(type: string): string {
		switch (type) {
			case 'all':
				return t2('All users (active + imported)');
			case 'filtered':
				return t2('Filtered by application');
			default:
				return type;
		}
	}
</script>

{#if loading}
	<div class="flex justify-center py-12">
		<Spinner size="10" />
	</div>
{:else if error}
	<AbstractCard class="max-w-none">
		<div class="py-8 text-center">
			<P class="text-red-500">{error}</P>
			<Button color="alternative" class="mt-4" onclick={goBack}>{$t("Back to Newsletters")}</Button>
		</div>
	</AbstractCard>
{:else if newsletter && staging}
	<div class="space-y-6">
		<!-- Header -->
		<div class="flex items-center justify-between">
			<div>
				<Heading tag="h3">{newsletter.name}</Heading>
				<P class="mt-1 text-gray-500 dark:text-gray-400">
					{$t("Review the details below before sending this newsletter.")}
				</P>
			</div>
			<Button color="alternative" onclick={goBack}>
				<svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
				</svg>
				{$t("Back")}
			</Button>
		</div>

		<!-- Campaign Details -->
		<AbstractCard class="max-w-none">
			<Heading tag="h4" class="mb-4">{$t("Campaign Details")}</Heading>
			<div class="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
				<div>
					<span class="font-semibold text-gray-700 dark:text-gray-300">{$t("Subject:")}</span>
					<span class="ml-2">{newsletter.subject}</span>
				</div>
				<div>
					<span class="font-semibold text-gray-700 dark:text-gray-300">{$t("Status:")}</span>
					<Badge color="blue" class="ml-2">{newsletter.status}</Badge>
				</div>
				<div>
					<span class="font-semibold text-gray-700 dark:text-gray-300">{$t("Audience:")}</span>
					<span class="ml-2">{audienceLabel(staging.audienceType)}</span>
				</div>
				<div>
					<span class="font-semibold text-gray-700 dark:text-gray-300">{$t("Master Template:")}</span>
					<span class="ml-2">{newsletter.masterTemplate?.templateName || $t('None')}</span>
				</div>
			</div>
		</AbstractCard>

		<!-- Recipient Breakdown -->
		<AbstractCard class="max-w-none">
			<Heading tag="h4" class="mb-4">{$t("Recipient Breakdown")}</Heading>
			<Table divClass="widget !p-1 relative" class="dataTable">
				<TableHead>
					<TableHeadCell>{$t("Category")}</TableHeadCell>
					<TableHeadCell class="text-right">{$t("Count")}</TableHeadCell>
				</TableHead>
				<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
					<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
						<TableBodyCell>
							<span class="text-green-600 dark:text-green-400">&#10003;</span> {$t("Eligible users")}
						</TableBodyCell>
						<TableBodyCell class="text-right font-semibold">{staging.eligibleUsers}</TableBodyCell>
					</TableBodyRow>
					{#if staging.bouncedUsersExcluded > 0}
						<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
							<TableBodyCell>
								<span class="text-red-500">&#10007;</span> {$t("Bounced users excluded")}
							</TableBodyCell>
							<TableBodyCell class="text-right font-semibold text-red-500"
								>{staging.bouncedUsersExcluded}</TableBodyCell
							>
						</TableBodyRow>
					{/if}
					{#if staging.optedOutUsersExcluded > 0}
						<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
							<TableBodyCell>
								<span class="text-yellow-500">&#10007;</span> {$t("Opted-out users excluded")}
							</TableBodyCell>
							<TableBodyCell class="text-right font-semibold text-yellow-500"
								>{staging.optedOutUsersExcluded}</TableBodyCell
							>
						</TableBodyRow>
					{/if}
				</TableBody>
			</Table>
			<Hr class="my-4" />
			<div class="flex items-center justify-between text-lg font-semibold">
				<span>{$t("Total Recipients")}</span>
				<span class="text-primary-600 dark:text-primary-400">{staging.totalRecipients}</span>
			</div>
		</AbstractCard>

		<!-- Batch Configuration & Timing -->
		<AbstractCard class="max-w-none">
			<Heading tag="h4" class="mb-4">{$t("Batch Configuration & Timing")}</Heading>
			<div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
				<div class="rounded-lg bg-depth p-4 text-center">
					<div class="text-2xl font-bold text-primary-600 dark:text-primary-400">{staging.waveSize}</div>
					<div class="mt-1 text-sm text-gray-500 dark:text-gray-400">{$t("Emails per batch")}</div>
				</div>
				<div class="rounded-lg bg-depth p-4 text-center">
					<div class="text-2xl font-bold text-primary-600 dark:text-primary-400">
						{staging.waveIntervalSeconds}s
					</div>
					<div class="mt-1 text-sm text-gray-500 dark:text-gray-400">{$t("Interval between batches")}</div>
				</div>
				<div class="rounded-lg bg-depth p-4 text-center">
					<div class="text-2xl font-bold text-primary-600 dark:text-primary-400">
						{staging.totalRecipients > 0
							? (staging.estimatedDurationSeconds / staging.totalRecipients).toFixed(1) + 's'
							: '—'}
					</div>
					<div class="mt-1 text-sm text-gray-500 dark:text-gray-400">{$t("Avg. interval per email")}</div>
				</div>
				<div class="rounded-lg bg-depth p-4 text-center">
					<div class="text-2xl font-bold text-primary-600 dark:text-primary-400">{staging.totalWaves}</div>
					<div class="mt-1 text-sm text-gray-500 dark:text-gray-400">{$t("Total batches")}</div>
				</div>
				<div class="rounded-lg bg-depth p-4 text-center">
					<div class="text-2xl font-bold text-primary-600 dark:text-primary-400">
						{staging.estimatedDuration}
					</div>
					<div class="mt-1 text-sm text-gray-500 dark:text-gray-400">{$t("Estimated duration")}</div>
				</div>
			</div>
		</AbstractCard>

		<!-- Warning banners -->
		{#if staging.bouncedUsersExcluded > 0}
			<div class="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
				<div class="flex items-start">
					<svg class="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
							clip-rule="evenodd"
						/>
					</svg>
					<div>
						<h3 class="font-semibold text-red-800 dark:text-red-300">{$t("Bounced Recipients Excluded")}</h3>
						<p class="mt-1 text-sm text-red-700 dark:text-red-400">
							{$t("{{count}} recipient(s) have been excluded due to previous delivery failures. These emails will not be sent to avoid further bounces and protect sender reputation.", { count: staging.bouncedUsersExcluded })}
						</p>
					</div>
				</div>
			</div>
		{/if}

		{#if staging.totalRecipients === 0}
			<div
				class="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20"
			>
				<div class="flex items-start">
					<svg
						class="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500"
						fill="currentColor"
						viewBox="0 0 20 20"
					>
						<path
							fill-rule="evenodd"
							d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
							clip-rule="evenodd"
						/>
					</svg>
					<div>
						<h3 class="font-semibold text-yellow-800 dark:text-yellow-300">{$t("No Recipients")}</h3>
						<p class="mt-1 text-sm text-yellow-700 dark:text-yellow-400">
							{$t("No recipients match the audience criteria. Adjust the audience settings before sending.")}
						</p>
					</div>
				</div>
			</div>
		{/if}

		<!-- Actions -->
		<AbstractCard class="max-w-none">
			<div class="flex flex-wrap items-center justify-between gap-3">
				<div class="flex gap-3">
					<Button color="alternative" onclick={doPreview}>
						<svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
							/>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
							/>
						</svg>
						{$t("Preview")}
					</Button>
					<Button color="alternative" onclick={doTestSend}>
						<svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
							/>
						</svg>
						{$t("Send Test Email")}
					</Button>
				</div>
				<div class="flex gap-3">
					<Button color="alternative" onclick={goBack}>{$t("Cancel")}</Button>
					<ConfirmButton
						color="green"
						confirmColor="green"
						disabled={staging.totalRecipients === 0 || sending}
						onconfirm={doSend}
					>
						{#if sending}
							<Spinner size="4" class="mr-2" />
						{/if}
						{$t("Send to {{count}} recipients", { count: staging.totalRecipients })}
					</ConfirmButton>
				</div>
			</div>
		</AbstractCard>

		<!-- Preview iframe -->
		{#if showPreview && previewHtml}
			<AbstractCard class="max-w-none">
				<div class="mb-4 flex items-center justify-between">
					<Heading tag="h4">{$t("Preview")}</Heading>
					<Button color="alternative" size="xs" onclick={() => (showPreview = false)}>{$t("Close")}</Button>
				</div>
				<div class="overflow-hidden rounded-lg border bg-white" style="min-height: 400px;">
					<iframe
						srcdoc={previewHtml}
						class="w-full"
						style="min-height: 400px; border: none;"
						title={$t("Newsletter Preview")}
					></iframe>
				</div>
			</AbstractCard>
		{/if}
	</div>
{/if}
